import { useState, useEffect } from 'react'
import { getTasks, createTask, updateTask, deleteTask } from '../api/client'
import { getTeamMembers } from '../api/client'
import Modal from '../components/Modal'
import { formatDate } from '../utils/weeks'
import { useTranslation } from 'react-i18next'

const STATUSES = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
]

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
]

const STATUS_STYLES = {
  todo: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  done: 'bg-emerald-100 text-emerald-700',
}

const PRIORITY_STYLES = {
  low: 'bg-gray-100 text-gray-500',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-red-100 text-red-600',
}

const EMPTY_FORM = { name: '', status: 'todo', priority: 'medium', responsible_id: '', deadline: '' }

function isOverdue(d) {
  if (!d) return false
  return new Date(d + 'T00:00:00') < new Date().setHours(0,0,0,0)
}
function isSoon(d) {
  if (!d) return false
  const diff = (new Date(d + 'T00:00:00') - new Date().setHours(0,0,0,0)) / 86400000
  return diff >= 0 && diff <= 7
}

export default function Tasks() {
  const { t } = useTranslation('engineering')
  const [tasks, setTasks] = useState([])
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null) // null | 'add' | { task }
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true)
    const [tasksData, membersData] = await Promise.all([
      getTasks(), getTeamMembers({ active: true })])
    setTasks(tasksData)
    setMembers(membersData)
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const openAdd = () => { setForm(EMPTY_FORM); setError(''); setModal('add') }
  const openEdit = (task) => {
    setForm({
      name: task.name,
      status: task.status,
      priority: task.priority,
      responsible_id: task.responsible_id ?? '',
      deadline: task.deadline ?? '',
    })
    setError('')
    setModal(task)
  }
  const closeModal = () => setModal(null)

  const handleSave = async () => {
    if (!form.name.trim()) { setError(t('tasks.nameRequired')); return }
    setSaving(true)
    try {
      const payload = {
        ...form,
        responsible_id: form.responsible_id ? Number(form.responsible_id) : null,
        deadline: form.deadline || null,
      }
      if (modal === 'add') {
        await createTask(payload)
      } else {
        await updateTask(modal.id, payload)
      }
      load()
      closeModal()
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed')
    }
    setSaving(false)
  }

  const handleDelete = async (task) => {
    if (!confirm(t('tasks.deleteConfirm', { name: task.name }))) return
    await deleteTask(task.id)
    load()
  }

  const statusLabel = (v) => {
    const key = v === 'todo' ? 'tasks.statuses.todo' : v === 'in_progress' ? 'tasks.statuses.inProgress' : v === 'done' ? 'tasks.statuses.done' : null
    return key ? t(key) : v
  }
  const priorityLabel = (v) => {
    const key = v === 'low' ? 'tasks.priorities.low' : v === 'medium' ? 'tasks.priorities.medium' : v === 'high' ? 'tasks.priorities.high' : null
    return key ? t(key) : v
  }

  return (
    <div className="p-8">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">{t('tasks.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('tasks.count', { count: tasks.length })}</p>
        </div>
        <button
          onClick={openAdd}
          className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          {t('tasks.newTask')}
        </button>
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm">{t('tasks.loading')}</div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-sm">{t('tasks.noTasks')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('tasks.nameHeader')}</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('tasks.statusHeader')}</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('tasks.priorityHeader')}</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('tasks.responsibleHeader')}</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">{t('tasks.deadlineHeader')}</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task, i) => (
                <tr
                  key={task.id}
                  className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${i === tasks.length - 1 ? 'border-b-0' : ''}`}
                >
                  <td className="px-5 py-3 font-medium text-gray-900">{task.name}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[task.status] || 'bg-gray-100 text-gray-600'}`}>
                      {statusLabel(task.status)}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${PRIORITY_STYLES[task.priority] || 'bg-gray-100 text-gray-500'}`}>
                      {priorityLabel(task.priority)}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-500">{task.responsible_name || '—'}</td>
                  <td className="px-5 py-3">
                    {task.deadline ? (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        task.status === 'done' ? 'bg-gray-100 text-gray-400' :
                        isOverdue(task.deadline) ? 'bg-red-100 text-red-600' :
                        isSoon(task.deadline) ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {formatDate(task.deadline)}{task.status !== 'done' && isOverdue(task.deadline) ? ' ⚠' : ''}
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => openEdit(task)} className="text-gray-400 hover:text-gray-900 transition-colors mr-3 text-xs font-medium">{t('common:actions.edit')}</button>
                    <button onClick={() => handleDelete(task)} className="text-gray-400 hover:text-red-500 transition-colors text-xs font-medium">{t('common:actions.delete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title={modal === 'add' ? t('tasks.addTaskModal') : t('tasks.editTaskModal')} onClose={closeModal}>
          <div className="space-y-4">
            {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.nameField')}</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder={t('tasks.taskNamePlaceholder')}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.statusField')}</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              >
                {STATUSES.map(s => <option key={s.value} value={s.value}>{statusLabel(s.value)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.priorityField')}</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
              >
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{priorityLabel(p.value)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.responsibleField')}</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={form.responsible_id}
                onChange={e => setForm(f => ({ ...f, responsible_id: e.target.value }))}
              >
                <option value="">{t('tasks.unassigned')}</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.deadlineField')}</label>
              <input
                type="date"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={form.deadline}
                onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={closeModal}
                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
